import React, { ReactNode } from 'react';
import { CalendarDays, ExternalLink, UserRound } from 'lucide-react';
import './TicketNotificationCard.css';

interface TicketNotificationCardProps {
    data: unknown;
    fallback: ReactNode;
    appearance?: "default" | "meeting-dark";
}

interface TicketNotificationData {
    ticketId: string;
    title: string;
    type: string;
    createdBy: string;
    priority: string;
    createdAt?: string;
    dueDate?: string | null;
    createdDate?: string;
    dueDateLabel?: string;
    url: string;
}

const parsePayload = (payload: unknown): TicketNotificationData | null => {
    let candidate = payload;

    for (let depth = 0; depth < 3 && typeof candidate === 'string'; depth += 1) {
        try {
            candidate = JSON.parse(candidate);
        } catch {
            return null;
        }
    }

    if (!candidate || typeof candidate !== 'object') return null;

    const value = candidate as Record<string, unknown>;
    const ticketId = String(value.ticketId ?? '').trim();
    const title = String(value.title ?? '').trim();
    const url = String(value.url ?? '').trim();

    if (!ticketId || !title || !url) return null;

    return {
        ticketId,
        title,
        url,
        type: String(value.type ?? 'Task').trim() || 'Task',
        createdBy: String(value.createdBy ?? 'Unknown user').trim() || 'Unknown user',
        priority: String(value.priority ?? 'UNSET').trim().toUpperCase() || 'UNSET',
        createdAt: typeof value.createdAt === 'string' ? value.createdAt : undefined,
        dueDate: typeof value.dueDate === 'string' ? value.dueDate : null,
        createdDate: typeof value.createdDate === 'string' ? value.createdDate : undefined,
        dueDateLabel: typeof value.dueDateLabel === 'string' ? value.dueDateLabel : undefined,
    };
};

const formatDate = (preferredLabel?: string, isoValue?: string | null): string => {
    const value = preferredLabel && preferredLabel !== '--' ? preferredLabel : isoValue;
    if (!value) return 'Not set';

    const numericDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    const date = numericDate
        ? new Date(Date.UTC(
            Number(numericDate[3]),
            Number(numericDate[1]) - 1,
            Number(numericDate[2])))
        : new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
};

const getPriorityClasses = (priority: string, dark: boolean): string => {
    if (priority === 'CRITICAL' || priority === 'URGENT') {
        return dark
            ? 'border-red-400/30 bg-red-400/15 text-red-200'
            : 'border-red-200 bg-red-50 text-red-700';
    }

    if (priority === 'HIGH') {
        return dark
            ? 'border-orange-400/30 bg-orange-400/15 text-orange-200'
            : 'border-orange-200 bg-orange-50 text-orange-700';
    }

    if (priority === 'MEDIUM') {
        return dark
            ? 'border-amber-400/30 bg-amber-400/15 text-amber-200'
            : 'border-amber-200 bg-amber-50 text-amber-700';
    }

    return dark
        ? 'border-slate-500/40 bg-slate-500/15 text-slate-200'
        : 'border-slate-200 bg-slate-100 text-slate-600';
};

const getSafeUrl = (value: string): string | null => {
    try {
        const url = new URL(value, window.location.origin);
        return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch {
        return null;
    }
};

const TicketNotificationCard: React.FC<TicketNotificationCardProps> = ({
    data,
    fallback,
    appearance = 'default',
}) => {
    const ticket = parsePayload(data);
    if (!ticket) return <>{fallback}</>;

    const dark = appearance === 'meeting-dark';
    const ticketUrl = getSafeUrl(ticket.url);
    const createdDate = formatDate(ticket.createdDate, ticket.createdAt);
    const dueDate = formatDate(ticket.dueDateLabel, ticket.dueDate);

    return (
        <article className={`ticket-notification-card chat-ticket-card min-w-0 ${dark ? 'ticket-notification-card--dark' : ''}`}>
            <div className="ticket-notification-card__header flex min-w-0 items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <span className={`ticket-notification-card__type rounded-md border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] ${dark ? 'border-purple-400/25 bg-purple-400/15 text-purple-200' : 'border-purple-200 bg-purple-50 text-[#6842A5]'}`}>
                            {ticket.type}
                        </span>
                        <span data-priority={ticket.priority} className={`ticket-notification-card__priority rounded-md border px-2 py-0.5 text-[9.5px] font-bold tracking-[0.08em] ${getPriorityClasses(ticket.priority, dark)}`}>
                            {ticket.priority}
                        </span>
                    </div>

                    <h3 className={`m-0 break-words text-[15px] font-semibold leading-5 ${dark ? 'text-slate-50' : 'text-slate-900'}`}>
                        {ticket.title}
                    </h3>
                    <p className={`ticket-notification-card__reference m-0 mt-1 text-[11.5px] font-semibold tracking-[0.02em] ${dark ? 'text-purple-300' : 'text-[#6F49B5]'}`}>
                        {ticket.ticketId}
                    </p>
                </div>

                {ticketUrl && (
                    <a
                        href={ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${ticket.ticketId}`}
                        className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold no-underline transition-colors ${dark ? 'border-purple-400/25 bg-purple-400/10 text-purple-200 hover:bg-purple-400/20 hover:text-purple-100' : 'border-purple-200 bg-purple-50 text-[#6842A5] hover:bg-purple-100 hover:text-[#57358E]'}`}
                    >
                        View issue
                        <ExternalLink size={12} strokeWidth={2.25} />
                    </a>
                )}
            </div>

            <div className={`ticket-notification-card__metadata mt-3 grid gap-px overflow-hidden rounded-lg border ${dark ? 'border-slate-700 bg-slate-700' : 'border-slate-200 bg-slate-200'}`}>
                <div className={`ticket-notification-card__metadata-item flex items-start gap-2 px-3 py-2.5 ${dark ? 'bg-slate-800/90' : 'bg-slate-50'}`}>
                    <UserRound size={14} className={`mt-0.5 shrink-0 ${dark ? 'text-slate-400' : 'text-slate-400'}`} />
                    <div className="min-w-0">
                        <div className={`ticket-notification-card__metadata-label whitespace-nowrap text-[9px] font-bold uppercase tracking-[0.08em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Reporter</div>
                        <div className={`ticket-notification-card__metadata-value truncate text-[11.5px] font-medium ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{ticket.createdBy}</div>
                    </div>
                </div>

                <div className={`ticket-notification-card__metadata-item flex items-start gap-2 px-3 py-2.5 ${dark ? 'bg-slate-800/90' : 'bg-slate-50'}`}>
                    <CalendarDays size={14} className={`mt-0.5 shrink-0 ${dark ? 'text-slate-400' : 'text-slate-400'}`} />
                    <div className="min-w-0">
                        <div className={`ticket-notification-card__metadata-label whitespace-nowrap text-[9px] font-bold uppercase tracking-[0.08em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Created</div>
                        <div className={`ticket-notification-card__metadata-value whitespace-nowrap text-[11.5px] font-medium ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{createdDate}</div>
                    </div>
                </div>

                <div className={`ticket-notification-card__metadata-item flex items-start gap-2 px-3 py-2.5 ${dark ? 'bg-slate-800/90' : 'bg-slate-50'}`}>
                    <CalendarDays size={14} className={`mt-0.5 shrink-0 ${dark ? 'text-slate-400' : 'text-slate-400'}`} />
                    <div className="min-w-0">
                        <div className={`ticket-notification-card__metadata-label whitespace-nowrap text-[9px] font-bold uppercase tracking-[0.08em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Due</div>
                        <div className={`ticket-notification-card__metadata-value ${dueDate === 'Not set' ? 'ticket-notification-card__metadata-value--empty' : ''} whitespace-nowrap text-[11.5px] font-medium ${dueDate === 'Not set' ? (dark ? 'text-slate-500' : 'text-slate-400') : (dark ? 'text-slate-200' : 'text-slate-700')}`}>{dueDate}</div>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default TicketNotificationCard;
